// src/components/student/DailyChallenge.jsx
// Picks one recommended quiz for the student today based on their weakest topic.
// Uses sessionStorage to avoid re-querying Firestore on the same day.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { HiLightningBolt, HiCheckCircle, HiArrowRight, HiFire } from "react-icons/hi";
import { db } from "../firebase/firebase";
import { isoDay, WEAK_THRESHOLD } from "../../lib/gamification";

function todayKey(uid) {
  return `challenge-${uid}-${isoDay(new Date())}`;
}

export default function DailyChallenge({ uid, profile, topicStats, attempts }) {
  const [challenge, setChallenge] = useState(null); // { id, topic, board, grade }
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.board) { setLoading(false); return; }

    // Check if today's challenge is already completed (an attempt taken today)
    const todayISO = isoDay(new Date());
    const todayMidnight = new Date(todayISO + "T00:00:00");

    async function load() {
      setLoading(true);
      try {
        // Try sessionStorage cache first
        const cached = sessionStorage.getItem(todayKey(uid));
        let mcqId, mcqTopic;

        if (cached) {
          const parsed = JSON.parse(cached);
          mcqId = parsed.id;
          mcqTopic = parsed.topic;
        } else {
          // Find weakest topic (lowest accuracy with ≥1 attempt)
          const weak = [...topicStats]
            .filter((t) => (t.attempts || 0) >= 1 && (t.accuracy ?? 1) < WEAK_THRESHOLD)
            .sort((a, b) => (a.accuracy ?? 1) - (b.accuracy ?? 1));

          const weakSubject = weak[0]?.subject || null;

          // Fetch MCQs matching student's board (client-filter grade to handle type mismatch)
          const snap = await getDocs(
            query(collection(db, "mcqs"), where("board", "==", profile.board))
          );
          let mcqs = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((m) => String(m.grade) === String(profile.grade));

          if (!mcqs.length) { setLoading(false); return; }

          // Prefer MCQs matching weak subject; fall back to any for board+grade
          const matched = weakSubject
            ? mcqs.filter((m) => m.subject === weakSubject || m.topic?.toLowerCase().includes(weakSubject?.toLowerCase()))
            : [];

          const pick = matched.length ? matched[0] : mcqs[Math.floor(Math.random() * mcqs.length)];
          mcqId = pick.id;
          mcqTopic = pick.topic || "Today's Quiz";

          sessionStorage.setItem(todayKey(uid), JSON.stringify({ id: mcqId, topic: mcqTopic }));
        }

        // Check if completed today
        const completedToday = attempts.some((a) => {
          const t = a.takenAt?.toDate ? a.takenAt.toDate() : new Date(a.takenAt);
          return a.mcqId === mcqId && t >= todayMidnight;
        });

        setChallenge({ id: mcqId, topic: mcqTopic });
        setDone(completedToday);
      } catch (e) {
        console.error("DailyChallenge load failed", e);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [uid, profile?.board, profile?.grade, topicStats, attempts]);

  if (loading || !challenge) return null;

  if (done) {
    return (
      <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 p-5 flex items-center gap-4 text-white shadow-lg shadow-blue-900/20 mb-6">
        <HiCheckCircle className="text-3xl shrink-0 text-blue-200" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-200">Daily Challenge</p>
          <p className="font-extrabold text-lg leading-tight">{challenge.topic}</p>
          <p className="text-blue-200 text-sm mt-0.5">Completed today! Come back tomorrow for a new challenge.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 p-5 flex items-center gap-4 text-white shadow-lg shadow-amber-900/20 mb-6">
      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
        <HiLightningBolt className="text-2xl" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-100">Daily Challenge</p>
        <p className="font-extrabold text-lg leading-tight truncate">{challenge.topic}</p>
        <p className="text-amber-100 text-xs mt-0.5 flex items-center gap-1">
          <HiFire className="text-sm" /> Tackle your weakest topic today — earn bonus XP!
        </p>
      </div>
      <Link
        to={`/mcq/${challenge.id}`}
        className="shrink-0 bg-white text-orange-600 font-bold text-sm px-4 py-2 rounded-xl hover:bg-amber-50 transition-colors flex items-center gap-1 whitespace-nowrap"
      >
        Start <HiArrowRight />
      </Link>
    </div>
  );
}
